import React, { useEffect, useCallback, FC } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getIndustriesAsync } from "src/redux/industries/actions";
import { selectIndustries } from "src/redux/industries/selectors";
import { getCloneTypesAsync } from "src/redux/cloneTypes/actions";
import { selectCloneTypes } from "src/redux/cloneTypes/selectors";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import {
  OnChangeInput,
  OnChangeRange,
  SetMultiSelectOptions,
  SetSelectOption,
} from "src/@types/form";
import { MinMaxValues } from "src/@types/filtering";
import { ReactComponent as FiltersIcon } from "src/assets/icons/filters.svg";
import { ReactComponent as CleanIcon } from "src/assets/icons/broom.svg";
import bagIcon from "src/assets/icons/bag.svg";
import cashIcon from "src/assets/icons/cash.svg";
import savingsIcon from "src/assets/icons/savings.svg";
import { useSearchParamsConverter } from "./hooks/useSearchParamsConverter";
import { getSelectedOptions } from "./utils/getSelectedOptions";
import { ShowMoreButton } from "./ShowMoreButton";
import { PriceRangeSliderFilteringItem } from "./PriceRangeSliderFilteringItem";
import { SingleButtonSelectorFilteringItem } from "./SingleButtonSelectorFilteringItem";
import { OutsideValuesMultiSelectFilteringItem } from "./OutsideValuesMultiSelectFilteringItem";
import { MultiButtonSelectorFilteringItem } from "./MultiButtonSelectorFilteringItem";
import { SearchFilteringItem } from "./SearchFilteringItem";
import {
  EMPLOYEE_COUNT_OPTIONS,
  YEAR_ON_THE_MARKET_OPTIONS,
  CUSTOMER_COUNT_OPTIONS,
  SEPARATOR,
  RANGE_SEPARATOR,
  generateQueryParamsSettingItems,
} from "./constants";
import {
  filteringButtonsTrns,
  filteringLabelsTrns,
  filteringTrns,
} from "./trns";
import { IQueryParamsData, QueryParamKeys } from "./types";
import { Button } from "../Button";
import { ButtonVariants } from "../Button/types";

const CURRENCY = "$";

interface Props {
  filtersValues?: MinMaxValues;
  pagePath?: string;
}

export const Filtering: FC<Props> = ({ filtersValues, pagePath }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    getQueryParam,
    setQueryParam,
    deleteQueryParam,
    setMultipleQueryParams,
  } = useQueryParams();

  const isShownMore = getQueryParam(QUERY_PARAM_KEYS.IS_SHOWED_MORE) === "true";

  const {
    max_monthly_income,
    min_monthly_income,
    max_annual_turnover,
    min_annual_turnover,
    max_investment_for_start,
    min_investment_for_start,
    max_cost,
    min_cost,
  } = filtersValues || {};

  const dispatch = useAppDispatch();

  const industries = useAppSelector(selectIndustries);
  const clonesTypes = useAppSelector(selectCloneTypes);

  useEffect(() => {
    if (!industries) {
      dispatch(getIndustriesAsync());
    }
  }, [dispatch, industries]);

  useEffect(() => {
    if (!clonesTypes) {
      dispatch(getCloneTypesAsync());
    }
  }, [dispatch, clonesTypes]);

  const queryParamSettingsItems =
    generateQueryParamsSettingItems(filtersValues);

  const {
    selectedListPrice,
    selectedIndustries,
    selectedBusinessType,
    selectedEmployeeCount,
    selectedCustomerCount,
    selectedMinInvestment,
    selectedMonthlyIncome,
    selectedAnnualTurnover,
    selectedYearsOnTheMarket,
    searchValue,
  } = useSearchParamsConverter<IQueryParamsData>(queryParamSettingsItems);

  const selectedIndustriesOptions = getSelectedOptions(
    selectedIndustries,
    industries?.data
  );
  const selectedBusinessTypeOptions = getSelectedOptions(
    selectedBusinessType,
    clonesTypes?.items
  );
  const selectedEmployeeCountOptions = getSelectedOptions(
    selectedEmployeeCount,
    EMPLOYEE_COUNT_OPTIONS
  );
  const selectedYearsOnTheMarketOptions = getSelectedOptions(
    selectedYearsOnTheMarket,
    YEAR_ON_THE_MARKET_OPTIONS
  );
  const selectedCustomerCountOptions = getSelectedOptions(
    selectedCustomerCount,
    CUSTOMER_COUNT_OPTIONS
  );

  const onChangeMultiSelect = useCallback(
    (queryParamKey: QueryParamKeys): SetMultiSelectOptions => {
      return (options) => {
        if (!options.length) {
          deleteQueryParam(queryParamKey);
          setQueryParam(QUERY_PARAM_KEYS.PAGE, 1);
        } else {
          const query = options.map((item) => item.value).join(SEPARATOR);
          setMultipleQueryParams({
            [queryParamKey]: query,
            [QUERY_PARAM_KEYS.PAGE]: 1,
          });
        }
      };
    },
    [setQueryParam, deleteQueryParam, setMultipleQueryParams]
  );

  const onChangeInput = useCallback(
    (queryParamKey: QueryParamKeys): OnChangeInput => {
      return (e) => {
        const { value } = e.target;
        if (!value) {
          deleteQueryParam(queryParamKey);
          setQueryParam(QUERY_PARAM_KEYS.PAGE, 1);
        } else {
          setMultipleQueryParams({
            [queryParamKey]: value,
            [QUERY_PARAM_KEYS.PAGE]: 1,
          });
        }
      };
    },
    [setQueryParam, deleteQueryParam, setMultipleQueryParams]
  );

  const onChangeRangeSlider = useCallback(
    (queryParamKey: QueryParamKeys): OnChangeRange => {
      return (min, max) => {
        setMultipleQueryParams({
          [queryParamKey]: `${min}${RANGE_SEPARATOR}${max}`,
          [QUERY_PARAM_KEYS.PAGE]: 1,
        });
      };
    },
    [setMultipleQueryParams]
  );

  const onClickButtonSelector = useCallback(
    (queryParamKey: QueryParamKeys): SetSelectOption => {
      return (option) => {
        const currentValue = getQueryParam(queryParamKey);
        const optionValue = option.value;

        if (currentValue === optionValue) {
          deleteQueryParam(queryParamKey);
          setQueryParam(QUERY_PARAM_KEYS.PAGE, 1);
        } else {
          setMultipleQueryParams({
            [queryParamKey]: optionValue,
            [QUERY_PARAM_KEYS.PAGE]: 1,
          });
        }
      };
    },
    [getQueryParam, deleteQueryParam, setQueryParam, setMultipleQueryParams]
  );

  const onRemoveFilters = () => navigate(pagePath);

  return (
    <div className="flex flex-col gap-5 gl:max-w-73.75 w-full p-5 gl:border border-black-base/20 rounded-20">
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <FiltersIcon />
        <span>{t(filteringTrns.title)}</span>
      </h3>

      <>
        <hr />
        <Button
          variant={ButtonVariants.SECONDARY}
          className="font-semibold gap-2"
          onClick={onRemoveFilters}
        >
          <CleanIcon width={20} height={20} />
          <span>{t(filteringButtonsTrns.remove)}</span>
        </Button>
      </>

      <hr />
      <SearchFilteringItem
        value={searchValue || ""}
        onChange={onChangeInput(QueryParamKeys.SEARCH)}
      />
      <hr />
      {filtersValues && (
        <>
          <PriceRangeSliderFilteringItem
            label={t(filteringLabelsTrns.minInvestment)}
            icon={{ url: savingsIcon }}
            initialMin={selectedMinInvestment[0]}
            initialMax={selectedMinInvestment[1]}
            min={min_investment_for_start?.toString()}
            max={max_investment_for_start?.toString()}
            currency={CURRENCY}
            onChange={onChangeRangeSlider(QueryParamKeys.MIN_INVESTMENT)}
          />
          <hr />
        </>
      )}

      {filtersValues && (
        <>
          <PriceRangeSliderFilteringItem
            label={t(filteringLabelsTrns.listPrice)}
            icon={{ url: cashIcon }}
            initialMin={selectedListPrice[0]}
            initialMax={selectedListPrice[1]}
            min={min_cost?.toString()}
            max={max_cost?.toString()}
            currency={CURRENCY}
            onChange={onChangeRangeSlider(QueryParamKeys.LIST_PRICE)}
          />
          <hr />
        </>
      )}

      <OutsideValuesMultiSelectFilteringItem
        label={t(filteringLabelsTrns.industry)}
        icon={{ url: bagIcon }}
        selectedOptions={selectedIndustriesOptions}
        options={industries?.data}
        setSelectedOptions={onChangeMultiSelect(QueryParamKeys.INDUSTRY)}
      />
      <hr />
      <MultiButtonSelectorFilteringItem
        label={t(filteringLabelsTrns.businessType)}
        icon={{ url: bagIcon }}
        options={clonesTypes?.items}
        activeOptions={selectedBusinessTypeOptions}
        setActiveOptions={onChangeMultiSelect(QueryParamKeys.BUSINESS_TYPE)}
      />
      <hr />
      {isShownMore && (
        <>
          <MultiButtonSelectorFilteringItem
            label={t(filteringLabelsTrns.employees)}
            icon={{ url: bagIcon }}
            options={EMPLOYEE_COUNT_OPTIONS}
            activeOptions={selectedEmployeeCountOptions}
            setActiveOptions={onChangeMultiSelect(
              QueryParamKeys.EMPLOYEE_COUNT
            )}
          />
          <hr />
          <MultiButtonSelectorFilteringItem
            label={t(filteringLabelsTrns.yearsOnTheMarket)}
            icon={{ url: bagIcon }}
            options={YEAR_ON_THE_MARKET_OPTIONS}
            activeOptions={selectedYearsOnTheMarketOptions}
            setActiveOptions={onChangeMultiSelect(
              QueryParamKeys.YEARS_ON_THE_MARKET
            )}
          />
          <hr />
          <SingleButtonSelectorFilteringItem
            label={t(filteringLabelsTrns.numberOfCustomers)}
            icon={{ url: bagIcon }}
            options={CUSTOMER_COUNT_OPTIONS}
            activeOption={selectedCustomerCountOptions?.value}
            setActiveOption={onClickButtonSelector(
              QueryParamKeys.CUSTOMER_COUNT
            )}
          />
          <hr />
          {filtersValues && (
            <>
              <PriceRangeSliderFilteringItem
                label={t(filteringLabelsTrns.annualTurnover)}
                icon={{ url: cashIcon }}
                initialMin={selectedAnnualTurnover[0]}
                initialMax={selectedAnnualTurnover[1]}
                min={min_annual_turnover?.toString()}
                max={max_annual_turnover?.toString()}
                currency={CURRENCY}
                onChange={onChangeRangeSlider(QueryParamKeys.ANNUAL_TURNOVER)}
              />
              <hr />
            </>
          )}
          {filtersValues && (
            <>
              <PriceRangeSliderFilteringItem
                label={t(filteringLabelsTrns.monthlyIncome)}
                icon={{ url: cashIcon }}
                initialMin={selectedMonthlyIncome[0]}
                initialMax={selectedMonthlyIncome[1]}
                min={min_monthly_income?.toString()}
                max={max_monthly_income?.toString()}
                currency={CURRENCY}
                onChange={onChangeRangeSlider(QueryParamKeys.MONTHLY_INCOME)}
              />
              <hr />
            </>
          )}
        </>
      )}

      <ShowMoreButton isShownMore={isShownMore} />
    </div>
  );
};
